<template>
  <div
    v-show="arrowVisible"
    class="annotations-arrow"
    :data-tid="`annotation-arrow-${annotation._id}`"
    :class="{
      faded: annotation?.approved < 0,
    }"
    :style="{
      top: `${0}px`,
      left: `${0}px`,
      width: `${500}px`,
      opacity: showCounterpointSelect && selectedInstruction._id !== annotation._id ? 0 : 1,
    }"
  >
    <svg xmlns="http://www.w3.org/2000/svg" class="annotations-svg" height="5">
      <defs>
        <!-- arrowhead marker definition -->
        <marker
          id="arrow"
          viewBox="0 0 10 10"
          refX="5"
          refY="5"
          markerWidth="8"
          markerHeight="8"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" />
        </marker>
      </defs>
      <path
        :stroke="color"
        :stroke-width="active ? 2 : 1"
        fill="transparent"
        :stroke-opacity="opacity"
        :d="arrowPath"
        :stroke-dasharray="annotation.parentAnn ? '5,5' : '0'"
        marker-start="url(#arrow)"
        @click="handleArrowClick"
        style="cursor: pointer"
      ></path>
    </svg>

    <!-- Thread Likes/Dislikes Counts Box -->
    <div
      v-if="showLikesCount"
      class="thread-likes-counts"
      :style="{
        top: `${threadLikesCountsTop}px`,
        'border-color': active ? '#0008' : '#0003',
      }"
      :class="{
        'like-btn-outline': mainPanelView === 'outline' && !showingCounterpointSidePanel,
        'like-btn-sidepanel-outline': mainPanelView === 'outline' && showingCounterpointSidePanel,
      }"
    >
      <SVGButton
        :icon="`thumbs-up`"
        title="Thread Likes"
        class="uk-padding-remove"
        :style="{
          opacity:
            active && threadLikesCounts[annotation._id].likes
              ? '1'
              : threadLikesCounts[annotation._id].likes
                ? '.5'
                : '0.25',
          border: 'none',
        }"
        size=".75em"
        @click="doLike()"
      >
        <span class="uk-text-small uk-margin-small-left">
          {{ threadLikesCounts[annotation._id].likes }}
        </span>
      </SVGButton>
      <SVGButton
        :icon="`thumbs-down`"
        title="Thread dislikes"
        class="uk-padding-remove"
        :style="{
          opacity:
            active && threadLikesCounts[annotation._id].dislikes
              ? '1'
              : threadLikesCounts[annotation._id].dislikes
                ? '.5'
                : '0.25',
          border: 'none',
        }"
        size=".75em"
        @click="doUnlike()"
      >
        <span class="uk-text-small uk-margin-small-left">
          {{ threadLikesCounts[annotation._id].dislikes }}
        </span>
      </SVGButton>
    </div>
    <!-- END Thread Likes/Dislikes Counts Box -->
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import SVGButton from '@/components/display/button/SVGButton.vue';
import RoundStatus from '@/assets/js/enum/RoundStatus';
import { EventBus } from '@/assets/js/event-bus';
import { throttleRaf } from '@/utils/throttleRaf';

export default {
  components: { SVGButton },
  name: 'AnnotationArrow',
  props: {
    /**
     * Source annotation assigned to this arrow.
     * The source of an arrow is always an annotation,
     * the target can be another annotation or selected text.
     */
    annotation: Object,

    type: String,

    outlineId: String,

    /**
     * Type of arrow:
     * text - arrow attached to document text.
     * inline - arrow attached to text inside an annotation.
     * block - arrow attached to an annotation box.
     * */
    arrowType: {
      type: String,
      default: 'text',
    },

    /** Arrow path color */
    color: {
      type: String,
      default: '#2D4756',
    },
  },

  data() {
    return {
      textHeight: 16,
      defaultOpacity: 0.7,
      arrowPath: '',
      // Cache DOM elements to avoid repeated queries
      cachedElements: {
        ann: null,
        refAnn: null,
        scrollerTop: null,
        refScrollerTop: null,
        refParent: null,
        outlineContainer: null,
        mainContainer: null,
      },
      // Cache computed values to avoid repeated calculations
      cachedGeometry: {
        lastUpdateTime: 0,
        refAnnotationPositions: null,
        annotationPositions: null,
        scrollDelta: 0,
      },
    };
  },

  watch: {
    scrollCounter() {
      this.updateArrowThrottled();
    },
    showCounterpointSelect() {
      this.updateArrowThrottled();
    },
    selectedInstruction: {
      handler() {
        this.updateArrowThrottled();
      },
      deep: true,
    },
    // Watch for annotation ID changes to refresh cached elements
    'annotation._id'() {
      this.refreshDomCache();
    },
  },

  computed: {
    ...mapState([
      'publicView',
      'threadLikesCounts',
      'showAnnotationLikes',
      'showingCounterpointSidePanel',
      'annotationFocusIds',
      'selectedItem',
      'mainPanelView',
      'annotationsVisible',
      'scrollCounter',
      'selectedInstruction',
      'showCounterpointSelect',
    ]),

    ...mapGetters([
      'getAnnotation',
      'useExcerpts',
      'getAnnotationRound',
      'getAnnotationChildCount',
      'roundCount',
    ]),

    arrowVisible() {
      return this.annotationsVisible[this.annotation._id]?.visible || this.useExcerpts;
    },

    opacity() {
      return !this.active ? this.defaultOpacity : 1;
    },

    /** Calculated likes count box top position */
    threadLikesCountsTop() {
      return this.sourceElTop - this.top + this.sourceHeight / 2 + this.textHeight * 2;
    },

    /** Show likes count box */
    showLikesCount() {
      if (!this.roundCount) return;

      const annotationRoundCounterpoint = this.getAnnotationRound(this.annotation._id);
      const isAnnotationRoundPublished =
        annotationRoundCounterpoint?.status === RoundStatus.PUBLISHED;
      return (
        !this.publicView &&
        this.showAnnotationLikes &&
        !this.annotation.refAnnotation._id &&
        this.threadLikesCounts[this.annotation._id] &&
        isAnnotationRoundPublished
      );
    },

    /** Boolean for whether to show this arrow in __active__ state */
    active() {
      if (this.selectedItem) {
        const validTypes = ['annotation', 'outlineNode', 'textNode'];
        if (!validTypes.includes(this.selectedItem.type)) {
          throw new Error('UI Error: Invalid annotation reference type');
        }
      }
      if (this.showCounterpointSelect && this.annotation._id === this.selectedInstruction._id) {
        return true;
      }
      return this.annotationFocusIds[this.annotation._id];
    },
  },

  mounted() {
    EventBus.on('panel.switch.update', this.panelUpdateThrottled);
    EventBus.on('click.citation.show.outline', this.outlineNodeUpdateArrowThrottled);
    EventBus.on('annotation.created', this.handleAnnotationCreated);
    EventBus.on('counterpoint.data.updated', this.handleCounterpointDataUpdated);
    EventBus.on('outline.paragraphs.updated', this.handleOutlineParagraphsUpdated);

    // Initial cache setup
    this.refreshDomCache();
    this.updateArrowThrottled();
  },

  created() {
    // Create throttled versions of methods
    this.updateArrowThrottled = throttleRaf(this.updateArrow);
    this.panelUpdateThrottled = throttleRaf(() => {
      this.refreshDomCache();
      this.updateArrow();
    });
    this.outlineNodeUpdateArrowThrottled = throttleRaf(this.updateArrow);

    window.addEventListener('resize', this.handleResize);
  },

  beforeUnmount() {
    EventBus.off('panel.switch.update', this.panelUpdateThrottled);
    EventBus.off('click.citation.show.outline', this.outlineNodeUpdateArrowThrottled);
    EventBus.off('annotation.created', this.handleAnnotationCreated);
    EventBus.off('counterpoint.data.updated', this.handleCounterpointDataUpdated);
    EventBus.off('outline.paragraphs.updated', this.handleOutlineParagraphsUpdated);
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    handleArrowClick(event) {
      const annotationElement = document.getElementById(`annotation-${this.annotation._id}`);
      if (annotationElement) {
        this.$emit('click', {
          event,
          annotation: this.annotation,
        });
      }
    },

    handleResize: throttleRaf(function () {
      this.refreshDomCache();
      this.updateArrow();
    }),

    /**
     * Cache DOM elements to avoid repeated queries
     */
    refreshDomCache() {
      const cache = this.cachedElements;

      // Cache main annotation elements
      cache.ann = document.getElementById(`annotation-${this.annotation._id}`);
      cache.scrollerTop = document.getElementById(`scroller_cp_${this.annotation.cpId}`);

      // Cache scroller containers
      cache.mainContainer = document.getElementById(
        this.mainPanelView === 'article'
          ? 'articleVirtualScrollContainer'
          : 'outlineVirtualScrollContainer'
      );
      cache.outlineContainer = document.getElementById('annotatorOutline');

      // Cache reference elements based on type
      this.cacheReferenceElements();

      // Reset geometry cache
      this.cachedGeometry.lastUpdateTime = 0;
    },

    /**
     * Cache reference elements based on arrow type
     */
    cacheReferenceElements() {
      const cache = this.cachedElements;

      if (this.arrowType === 'block') {
        cache.refAnn = document.getElementById(`annotation-${this.annotation.refAnnotation._id}`);
        if (this.annotation.refAnnotation?._id) {
          const refAnnotation = this.getAnnotation(this.annotation.refAnnotation._id);
          if (refAnnotation?.cpId) {
            cache.refScrollerTop = document.getElementById(`scroller_cp_${refAnnotation.cpId}`);
          }
        }

        return;
      }

      if (this.type === 'root') {
        cache.refAnn = document.getElementById(
          `root-${this.annotation.parentAnn || this.annotation._id}`
        );
        return;
      }

      if (this.type === 'text' || this.arrowType === 'inline') {
        let refAnns = Array.from(
          document.getElementsByClassName(`counterpoint-ref-${this.annotation._id}`)
        );
        let noderOrder = [];

        // Filter elements based on view ('text' vs 'inline')
        const isInline = this.arrowType === 'inline';
        let visibleAnns = refAnns.filter(el => {
          const id = el.id || '';
          return id.includes(this.mainPanelView) || (isInline && id.includes('annotation'));
        });

        // Special handling for outline view
        if (this.type === 'text' && this.mainPanelView === 'outline' && this.outlineId) {
          const outlineParent = document.getElementById(`outline_P_${this.outlineId}`);
          if (outlineParent) {
            const childAnns = Array.from(outlineParent.children);
            visibleAnns = childAnns.filter(
              child =>
                child.classList.contains(`counterpoint-ref-${this.annotation._id}`) &&
                (child.id || '').includes(this.mainPanelView)
            );
          }
        }

        if (visibleAnns.length === 0) {
          cache.refAnn = null;
          return;
        }

        // Filter out elements with zero dimensions (duplicates that aren't rendered)
        const elementsWithDimensions = visibleAnns.filter(
          el => el.offsetWidth > 0 && el.offsetHeight > 0
        );

        if (elementsWithDimensions.length === 0) {
          cache.refAnn = null;
          return;
        }

        // Find the minimum offsetTop among elements with dimensions
        elementsWithDimensions.forEach(el => noderOrder.push(el.offsetTop));
        const minOffsetTop = Math.min(...noderOrder);

        // Filter for elements on the top-most line
        const topMostAnns = elementsWithDimensions.filter(el => el.offsetTop === minOffsetTop);

        // Select the middle element from the top-most line
        cache.refAnn = topMostAnns[Math.floor(topMostAnns.length / 2)];

        if (this.arrowType === 'inline' && this.annotation.refAnnotation?._id) {
          const refAnnotation = this.getAnnotation(this.annotation.refAnnotation._id);
          if (refAnnotation?.cpId) {
            cache.refScrollerTop = document.getElementById(`scroller_cp_${refAnnotation.cpId}`);
          }
        } else if (this.type === 'text') {
          cache.refScrollerTop = this.cachedElements.mainContainer;
        }
      }
    },

    /**
     * Get cached or compute geometry for elements
     */
    getElementGeometry() {
      const now = Date.now();
      const cache = this.cachedGeometry;

      // Use cached geometry if computed within last 16ms (one frame)
      if (
        now - cache.lastUpdateTime < 16 &&
        cache.refAnnotationPositions &&
        cache.annotationPositions
      ) {
        return cache;
      }

      const { ann, refAnn, scrollerTop, refScrollerTop } = this.cachedElements;

      if (!ann || !refAnn) return null;

      // Read geometry once per frame
      cache.refAnnotationPositions = refAnn.getBoundingClientRect();
      cache.annotationPositions = ann.getBoundingClientRect();
      cache.scrollDelta =
        parseInt(scrollerTop?.style?.top || 0) - parseInt(refScrollerTop?.style?.top || 0);
      cache.lastUpdateTime = now;

      return cache;
    },

    /** Recalculate arrow path */
    updateArrow() {
      const { ann, refAnn, scrollerTop } = this.cachedElements;

      if (!scrollerTop || !ann || !refAnn) {
        return;
      }

      // Check if refAnn is still in the DOM (not detached)
      if (!document.contains(refAnn)) {
        this.refreshDomCache();
        // Try again with fresh cache
        const { refAnn: freshRefAnn } = this.cachedElements;
        if (!freshRefAnn || !document.contains(freshRefAnn)) {
          return;
        }
      }

      const geometry = this.getElementGeometry();
      if (!geometry) {
        return;
      }

      const { refAnnotationPositions, annotationPositions, scrollDelta } = geometry;

      // handle arrows linked to text (inside documents or annotations)
      if (this.arrowType !== 'block') {
        let refParent = refAnn.parentElement?.parentElement?.parentElement;
        if (this.type === 'ann') {
          refParent = refAnn.parentElement?.parentElement?.parentElement?.parentElement;
        }

        // More conservative fallback logic for refParent
        if (!refParent) {
          // Check if refAnn has ANY parent at all
          if (!refAnn.parentElement) {
            this.refreshDomCache();
            // Try once more with fresh elements
            const { refAnn: newRefAnn } = this.cachedElements;
            if (!newRefAnn || !newRefAnn.parentElement) {
              return;
            }
            // Update our local reference
            const updatedRefAnn = newRefAnn;
            refParent = updatedRefAnn.parentElement?.parentElement?.parentElement;
            if (this.type === 'ann') {
              refParent = updatedRefAnn.parentElement?.parentElement?.parentElement?.parentElement;
            }
          }

          // Log the actual DOM structure to understand what we're working with
          if (refAnn.parentElement) {
            let currentElement = refAnn;
            // Try to find a reasonable parent by going up the tree
            currentElement = refAnn;
            for (let depth = 1; depth <= 5 && currentElement.parentElement; depth++) {
              currentElement = currentElement.parentElement;
              // Look for elements that are likely to be proper containers
              if (currentElement.offsetWidth > 200 && currentElement.offsetHeight > 50) {
                refParent = currentElement;
                break;
              }
            }

            // If still no parent, use the direct parent of refAnn
            if (!refParent && refAnn.parentElement) {
              refParent = refAnn.parentElement;
            }
          }
        }

        if (!refParent) {
          return;
        }

        // Cache parent element and its computed style
        if (!this.cachedElements.refParent || this.cachedElements.refParent !== refParent) {
          this.cachedElements.refParent = refParent;
          this.cachedElements.refParentStyle = window.getComputedStyle(refParent);
        }

        const paddingLeft = parseInt(this.cachedElements.refParentStyle.paddingLeft || 0);

        if (this.type === 'text' && this.mainPanelView === 'outline') {
          const outlineParent = document.getElementById(`outline_P_${this.outlineId}`);
          if (outlineParent) {
            refParent = outlineParent;
          }
        }

        const start_x = refAnn.offsetLeft + refAnn.offsetWidth / 2 + paddingLeft;
        const start_y =
          refAnn.offsetTop +
          (this.type === 'text' || this.arrowType === 'inline'
            ? refAnn.parentElement?.parentElement?.offsetTop || 0
            : 15);
        const end_x = annotationPositions.left - refParent.getBoundingClientRect().left;

        let end_y = annotationPositions.height / 2;

        if (!this.useExcerpts) {
          end_y += ann.offsetTop - refParent.offsetTop;
        }
        end_y += scrollDelta;

        if (this.type === 'text' && this.mainPanelView === 'outline') {
          end_y +=
            refAnn.offsetTop +
            (this.cachedElements.outlineContainer?.getBoundingClientRect().top -
              refAnnotationPositions.top || 0) +
            20;
        }

        if (ann.style.top && this.useExcerpts) {
          const annotationTop = parseInt(ann.style.top);
          const childCount = this.annotationChildCount(
            this.annotation?.refAnnotation?._id || this.annotation._id
          );
          if (childCount > 1) {
            end_y += annotationTop - refParent.offsetTop;
          } else if (this.type === 'text' && this.mainPanelView === 'outline') {
            end_y += annotationTop;
          }
        }

        const sidePanelOffset = this.showingCounterpointSidePanel
          ? 4
          : this.type === 'ann' && this.arrowType === 'inline'
            ? 0
            : 17;
        this.arrowPath = `M ${start_x} ${start_y} H ${refParent.offsetWidth} C ${end_x - (end_x * sidePanelOffset) / 100} ${start_y} ${end_x - (end_x * (this.showingCounterpointSidePanel ? 4 : 17)) / 100} ${end_y} ${end_x} ${end_y}`;
      } else {
        // handle arrows linked to block elements (annotation boxes)
        const start_x = refAnnotationPositions.width;
        const start_y = refAnnotationPositions.height / 2;
        const end_x = annotationPositions.left - refAnnotationPositions.left;

        let end_y = annotationPositions.height / 2;

        if (!this.useExcerpts) {
          end_y += ann.offsetTop - refAnn.offsetTop;
        }
        end_y += scrollDelta;

        if (
          ann.style.top &&
          this.annotationChildCount(this.annotation?.refAnnotation?._id || this.annotation._id) >
            1 &&
          this.useExcerpts
        ) {
          end_y += parseInt(ann.style.top) - refAnn.offsetTop;
        }

        const sidePanelOffset = this.showingCounterpointSidePanel ? 4 : 0;
        this.arrowPath = `M ${start_x} ${start_y} C ${end_x - (end_x * sidePanelOffset) / 100} ${start_y} ${end_x - (end_x * (this.showingCounterpointSidePanel ? 4 : 17)) / 100} ${end_y} ${end_x} ${end_y}`;
      }
    },

    annotationChildCount(id) {
      return this.getAnnotationChildCount(id);
    },

    handleLikeView() {
      const elements = document.getElementsByClassName(`counterpoint-ref-${this.annotation._id}`);
      const targetElement = this.showingCounterpointSidePanel ? elements[2] : elements[0];

      if (!targetElement) return 0;

      let parent = targetElement;
      for (let i = 0; i < 7 && parent; i++) {
        parent = parent.parentElement;
      }

      return parent?.style.paddingLeft === '36px' ? 36 : 0;
    },

    handleAnnotationCreated() {
      this.refreshDomCache();
      // Ensure DOM is updated
      this.$nextTick(() => {
        this.updateArrowThrottled();
      });
    },

    handleCounterpointDataUpdated() {
      this.refreshDomCache();
      // Ensure DOM is updated
      this.$nextTick(() => {
        this.updateArrowThrottled();
      });
    },

    handleOutlineParagraphsUpdated() {
      this.refreshDomCache();
      this.updateArrowThrottled();
    },
  },
};
</script>

<style lang="scss" scoped>
.thread-likes-counts {
  background: white;
  position: absolute;
  right: 0.5rem;
  border: 1px solid #0008;
  border-radius: 4px;
  padding: 0.5rem;
  transition: all 0.5s ease;

  @media only screen and (max-width: 1400px) {
    right: 0.25rem;
  }
}

.like-btn-outline {
  right: 3rem;
}

.like-btn-sidepanel-outline {
  right: 27rem;

  @media only screen and (max-width: 4500px) {
    right: 59rem;
  }

  @media only screen and (max-width: 4400px) {
    right: 58rem;
  }

  @media only screen and (max-width: 4200px) {
    right: 55rem;
  }

  @media only screen and (max-width: 4000px) {
    right: 52.5rem;
  }

  @media only screen and (max-width: 3800px) {
    right: 50rem;
  }

  @media only screen and (max-width: 3600px) {
    right: 48rem;
  }

  @media only screen and (max-width: 3400px) {
    right: 45rem;
  }

  @media only screen and (max-width: 3200px) {
    right: 42.5rem;
  }

  @media only screen and (max-width: 3000px) {
    right: 40rem;
  }

  @media only screen and (max-width: 2800px) {
    right: 37rem;
  }

  @media only screen and (max-width: 2500px) {
    right: 34rem;
  }

  @media only screen and (max-width: 2200px) {
    right: 30rem;
  }

  @media only screen and (max-width: 1536px) {
    right: 22.5rem;
  }

  @media only screen and (max-width: 1440px) {
    right: 21.5rem;
  }

  @media only screen and (max-width: 1024px) {
    right: 15.5rem;
  }
}
</style>
